import slugify from "slugify";

/**
 * Configuration options for slug generation
 */
interface SlugOptions {
  /** The separator character to use (default: '-') */
  separator?: string;
  /** Whether to convert to lowercase (default: true) */
  lowercase?: boolean;
  /** Maximum length of the generated slug */
  maxLength?: number;
  /** Whether to trim leading/trailing separators (default: true) */
  trim?: boolean;
  /** Additional custom replacements */
  customReplacements?: Array<[string, string]>;
}

/**
 * Универсальный помощник для генерации slug из любого текста
 * Поддерживает транслитерацию греческих, кириллических и других символов
 *
 * @param input - Входной текст для преобразования в slug
 * @param options - Опциональные настройки для генерации slug
 * @returns URL-friendly slug строка
 *
 * @example
 * generateSlug('A.Symmetron') // 'a-symmetron'
 * generateSlug('Vesta, Voula 2') // 'vesta-voula-2'
 * generateSlug('MG Houvardas, Ag. Aggelon 2') // 'mg-houvardas-ag-aggelon-2'
 * generateSlug('Αγίων Αγγέλων 3') // 'agion-aggelon-3'
 * generateSlug('Принц Петру') // 'princ-petru'
 * generateSlug('Димитракопулу') // 'dimitrakopulu'
 */
export const generateSlug = (
  input: string,
  options: SlugOptions = {},
): string => {
  if (!input || typeof input !== "string") {
    return "";
  }

  const {
    separator = "-",
    lowercase = true,
    maxLength,
    trim = true,
    customReplacements = [],
  } = options;

  // Apply custom replacements first
  let processedInput = input;
  for (const [search, replace] of customReplacements) {
    processedInput = processedInput.replace(new RegExp(search, "g"), replace);
  }

  // Configure slugify with comprehensive settings
  const slug = slugify(processedInput, {
    replacement: separator,
    lower: lowercase,
    strict: false, // Keep Unicode characters for better transliteration
    locale: "en", // English locale for consistent transliteration
    trim: trim,
    remove: /[*+~.()'"!:@?%^&;]/g, // Remove problematic characters but keep commas for proper replacement
  });

  // Handle specific character replacements that slugify might not handle well
  let finalSlug = slug
    // Replace any remaining commas with separator
    .replace(/,/g, separator)
    // Replace dots with separator (for cases like A.Symmetron)
    .replace(/\./g, separator)
    // Clean up multiple separators
    .replace(new RegExp(`${separator}+`, "g"), separator);

  // Trim separators from start and end if trim is enabled
  if (trim) {
    const separatorRegex = new RegExp(`^${separator}+|${separator}+$`, "g");
    finalSlug = finalSlug.replace(separatorRegex, "");
  }

  // Apply max length if specified
  if (maxLength && finalSlug.length > maxLength) {
    // Find last separator before maxLength to avoid cutting words
    const truncated = finalSlug.substring(0, maxLength);
    const lastSeparatorIndex = truncated.lastIndexOf(separator);

    finalSlug =
      lastSeparatorIndex > 0
        ? truncated.substring(0, lastSeparatorIndex)
        : truncated;
  }

  return finalSlug;
};

/**
 * Проверяет, представляет ли строка MongoDB ObjectId
 * @param id Строка для проверки
 * @returns Boolean
 */
export const isValidMongoId = (id: string): boolean => {
  const MONGO_ID_REGEX = /^[a-fA-F0-9]{24}$/;
  return MONGO_ID_REGEX.test(id);
};

/**
 * Извлекает MongoDB ID из slug, если он присутствует
 * @param slug Входная строка slug
 * @returns MongoDB ID или null, если не найден
 */
export const getIdFromSlug = (slug: string): string | null => {
  if (!slug) {
    return null;
  }

  const MONGO_ID_REGEX = /^[a-fA-F0-9]{24}$/;

  // Split by separator but handle case when there's no separator
  const parts = slug.split("-");
  if (parts.length === 0) {
    return null;
  }

  // The last segment should be the ID in the `${titleSlug}-{id}` pattern
  const idCandidate = parts[parts.length - 1];

  // Validate that the candidate matches the MongoDB ObjectId format
  if (MONGO_ID_REGEX.test(idCandidate)) {
    return idCandidate;
  }

  // If we couldn't find a valid ID at the end, try to search for a MongoDB ID pattern anywhere in the slug
  const idMatch = slug.match(/[a-fA-F0-9]{24}/);
  if (idMatch && idMatch[0]) {
    return idMatch[0];
  }

  return null;
};

/**
 * @deprecated Use generateSlug instead
 * Generates slug with MongoDB ID appended
 */
export const getSlug = ({ title, id }: { title: string; id: string }) => {
  if (!title || !id) {
    return "";
  }
  return `${generateSlug(title)}-${id}`;
};

/**
 * @deprecated Use generateSlug instead
 * Legacy function for backward compatibility
 */
export const titleToSlug = (
  title: string,
  options?: { lower?: boolean; appendIndex?: number },
) => {
  if (!title) {
    return "";
  }

  const slug = generateSlug(title, {
    lowercase: options?.lower !== false,
    separator: "-",
  });

  // Add index if specified
  if (options?.appendIndex !== undefined) {
    return `${slug}-${options.appendIndex}`;
  }

  return slug;
};

// Default export for convenience
export default generateSlug;
